import { useCallback, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

export type OrganizationUserRole = 'admin' | 'analyst' | 'viewer';
export type OrganizationUserStatus = 'active' | 'inactive' | 'invited' | 'suspended';
export type OrganizationInviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface OrganizationUser {
  uid: string;
  id?: string;
  email: string;
  displayName?: string;
  role: OrganizationUserRole;
  status: OrganizationUserStatus;
  organizationId?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
  lastActive?: unknown;
  lastLogin?: unknown;
}

export interface OrganizationInvite {
  id: string;
  email: string;
  displayName?: string;
  role: OrganizationUserRole;
  status: OrganizationInviteStatus;
  invitedBy?: string;
  invitedByEmail?: string;
  createdAt?: unknown;
  expiresAt?: unknown;
  emailSentAt?: unknown;
  deliveryStatus?: 'queued' | 'sent' | 'failed';
}

interface ListOrganizationUsersResponse {
  users?: OrganizationUser[];
  invitations?: OrganizationInvite[];
  invites?: OrganizationInvite[];
  pendingInvites?: OrganizationInvite[];
}

interface MutationResponse {
  success?: boolean;
  message?: string;
  invitationId?: string;
}

function normalizeUser(user: OrganizationUser): OrganizationUser {
  return {
    ...user,
    uid: user.uid || user.id || '',
    status: user.status || 'active',
  };
}

function getPendingInvites(data: ListOrganizationUsersResponse): OrganizationInvite[] {
  const invites = data.pendingInvites || data.invitations || data.invites || [];
  return invites.filter((invite) => (invite.status || 'pending') === 'pending');
}

export function useOrganizationUsers(enabled: boolean) {
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [pendingInvites, setPendingInvites] = useState<OrganizationInvite[]>([]);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadIdRef = useRef(0);

  const loadOrganizationUsers = useCallback(async () => {
    if (!enabled) {
      setUsers([]);
      setPendingInvites([]);
      return;
    }

    const loadId = loadIdRef.current + 1;
    loadIdRef.current = loadId;
    setLoading(true);
    setError(null);

    try {
      const fn = httpsCallable<Record<string, never>, ListOrganizationUsersResponse>(
        functions,
        'listOrganizationUsers'
      );
      const result = await fn({});
      if (loadIdRef.current === loadId) {
        setUsers((result.data.users || []).map(normalizeUser));
        setPendingInvites(getPendingInvites(result.data));
      }
      return result.data;
    } catch (err: any) {
      const message = err.message || 'Failed to load organization users';
      if (loadIdRef.current === loadId) {
        setError(message);
      }
      throw err;
    } finally {
      if (loadIdRef.current === loadId) {
        setLoading(false);
      }
    }
  }, [enabled]);

  const runMutation = useCallback(async <TData,>(
    functionName: string,
    payload: TData,
    successMessage?: string
  ) => {
    setMutating(true);
    setError(null);
    try {
      const fn = httpsCallable<TData, MutationResponse>(functions, functionName);
      const result = await fn(payload);
      await loadOrganizationUsers();
      return {
        ...result.data,
        message: result.data.message || successMessage,
      };
    } catch (err: any) {
      const message = err.message || 'Organization user action failed';
      setError(message);
      throw err;
    } finally {
      setMutating(false);
    }
  }, [loadOrganizationUsers]);

  const inviteUser = useCallback(async (payload: {
    email: string;
    displayName?: string;
    role: OrganizationUserRole;
  }) => {
    return runMutation('inviteOrganizationUser', payload, 'Invitation sent');
  }, [runMutation]);

  const promoteUserToAdmin = useCallback(async (userId: string) => {
    return runMutation('updateOrganizationUserRole', {
      targetUserId: userId,
      userId,
      role: 'admin' as const,
    }, 'User promoted to admin');
  }, [runMutation]);

  const removeUser = useCallback(async (userId: string) => {
    return runMutation('removeOrganizationUser', {
      targetUserId: userId,
      userId,
    }, 'User removed');
  }, [runMutation]);

  const revokeInvite = useCallback(async (inviteId: string) => {
    return runMutation('revokeOrganizationInvite', { inviteId }, 'Invitation revoked');
  }, [runMutation]);

  const resendInvite = useCallback(async (inviteId: string) => {
    return runMutation('resendOrganizationInvite', { inviteId }, 'Invitation resent');
  }, [runMutation]);

  return {
    users,
    pendingInvites,
    loading,
    mutating,
    error,
    loadOrganizationUsers,
    inviteUser,
    promoteUserToAdmin,
    removeUser,
    revokeInvite,
    resendInvite,
  };
}
